/** CSPRNG helpers. Everything random in the vault comes from here. */

export function randomBytes(length: number): Uint8Array {
  const out = new Uint8Array(length);
  crypto.getRandomValues(out);
  return out;
}

/**
 * A uniform integer in [0, max) by rejection sampling.
 *
 * The naive `% max` is biased whenever max is not a power of two, and for a
 * 6906-word list that bias is measurable. Drawing into the next power of two
 * and discarding out-of-range values is unbiased for any max.
 */
export function randomBelow(max: number): number {
  if (!Number.isInteger(max) || max <= 0 || max > 2 ** 32) {
    throw new Error('randomBelow: max out of range');
  }
  if (max === 1) return 0;

  const bits = Math.ceil(Math.log2(max));
  const bytes = Math.ceil(bits / 8);
  const mask = (1 << bits) - 1;

  // Draw in small batches so a run of rejections costs one syscall, not many.
  for (;;) {
    const buf = randomBytes(bytes * 16);
    for (let i = 0; i + bytes <= buf.length; i += bytes) {
      let value = 0;
      for (let j = 0; j < bytes; j++) value = (value << 8) | buf[i + j]!;
      value &= mask;
      if (value < max) return value;
    }
  }
}

/** Fisher–Yates over a copy, using the same unbiased source. */
export function shuffled<T>(items: readonly T[]): T[] {
  const out = [...items];
  for (let i = out.length - 1; i > 0; i--) {
    const j = randomBelow(i + 1);
    [out[i], out[j]] = [out[j]!, out[i]!];
  }
  return out;
}

/** An opaque, URL-safe id. Used for anchor ids and idempotency keys. */
export function randomId(bytes = 16): string {
  const alphabet = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789-_';
  const raw = randomBytes(bytes);
  let out = '';
  for (const b of raw) out += alphabet[b & 63];
  return out;
}
